import React from "react";
import type {
  ActivityHistoryItem,
  ActivityHistoryState,
  ActivityHistoryValue,
  ResultItem,
  StatusTypes,
  ValueType,
} from "./types";
import { Assets, AttributeSchemaBase } from "../../../../types/types.PIM";
import type { TFunction } from "react-i18next";
import { BUYER_CUSTOM_SKU_ATTRIBUTE_NAME, isMultiValue } from "./utils";
import type { ActivityTargetType } from "../../../../util/activityEndpoints";
import type { EntityType } from "../../../../types/types.MDM";
import { getMdmEntityLabel } from "../../../../util/mdmEntityDisplayNames";
import type { MdmEntityDisplayNameForms } from "../../../../util/mdmEntityDisplayNames";

export function hasGroupData(item: ActivityHistoryItem): boolean {
  return (
    Boolean(item.state_before?.values?.length) ||
    Boolean(item.state_after?.values?.length) ||
    Boolean(item.state_before?.attributes?.length) ||
    Boolean(item.state_after?.attributes?.length) ||
    item.state_before?.allow_custom_sku !== undefined ||
    item.state_after?.allow_custom_sku !== undefined ||
    item.state_before?.list_price_visibility !== undefined ||
    item.state_after?.list_price_visibility !== undefined ||
    Boolean(item.state_before?.assets?.length) ||
    Boolean(item.state_after?.assets?.length) ||
    Boolean(item.state_before?.asset) ||
    Boolean(item.state_after?.asset) ||
    item.state_before?.external_link !== undefined ||
    item.state_after?.external_link !== undefined ||
    Boolean(item.state_after?.parent_entity) ||
    Boolean(item.state_before?.parent_entity) ||
    Boolean(item.state_after?.subsidiary_actions?.length) ||
    Boolean(item.state_before?.subsidiary_actions?.length)
  );
}

function collectAttributes(
  state_before: ActivityHistoryState,
  state_after: ActivityHistoryState
): AttributeSchemaBase[] {
  const attributes: AttributeSchemaBase[] = [];
  const seen = new Set<string>();

  const add = (list?: AttributeSchemaBase[]) => {
    list?.forEach((attr) => {
      if (attr?.id && !seen.has(attr.id)) {
        attributes.push(attr);
        seen.add(attr.id);
      }
    });
  };

  add(state_after.attributes);
  add(state_before.attributes);

  return attributes;
}

function collectAttributeValues(
  values: ActivityHistoryValue[],
  attributeId: string,
  keepAllValues: boolean
): ActivityHistoryValue[] {
  const matches = values.filter((value) => value.attribute_id === attributeId);

  if (keepAllValues) {
    return matches.sort((a, b) => {
      const aModified = a.modified_at || "";
      const bModified = b.modified_at || "";
      return bModified.localeCompare(aModified);
    });
  }

  return matches;
}

function valuesAreDifferent(
  before: ActivityHistoryValue[],
  after: ActivityHistoryValue[]
): boolean {
  if (before.length !== after.length) return true;

  const beforeStr = before
    .map((v) => JSON.stringify(v.value))
    .sort()
    .join("|");
  const afterStr = after
    .map((v) => JSON.stringify(v.value))
    .sort()
    .join("|");

  return beforeStr !== afterStr;
}

function hasMeaningfulValue(
  value: ActivityHistoryValue | ActivityHistoryValue[] | null
): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) {
    return (
      value.length > 0 &&
      value.some((v) => {
        const val = v?.value;
        return val !== null && val !== undefined && val !== "";
      })
    );
  }
  const val = value?.value;
  return val !== null && val !== undefined && val !== "";
}

function allowCustomSkuToValue(
  value: boolean | undefined
): ActivityHistoryValue | null {
  if (value === undefined) return null;
  return {
    attribute_id: "allow_custom_sku",
    data_type: "boolean",
    id: `allow_custom_sku-${value}`,
    priority: 0,
    value: value,
  };
}

export function getVisibilityLabel(visibility: string, t: TFunction): string {
  switch (visibility) {
    case "available_everywhere":
      return t("Available Everywhere");
    case "internal_and_customers":
      return t("Internal Users and Customers");
    case "internal_users":
      return t("Internal Users");
    default:
      return visibility;
  }
}

function listPriceVisibilityToValue(
  value: string | undefined,
  t: TFunction
): ActivityHistoryValue | null {
  if (value === undefined) return null;
  return {
    attribute_id: "list_price_visibility",
    data_type: "text",
    id: `list_price_visibility-${value}`,
    priority: 0,
    value: getVisibilityLabel(value, t),
  };
}

function processGroupItem(
  item: ActivityHistoryItem,
  t: TFunction
): ResultItem | null {
  if (!hasGroupData(item)) {
    return null;
  }

  const { state_before, state_after } = item;
  const subItems: ResultItem["subItems"] = [];

  let hasStatusChange = false;
  let hasSkuConfigChange = false;
  let hasDocumentsChange = false;
  let hasPricingVisibilityChange = false;

  const beforeValues = Array.isArray(state_before.values)
    ? state_before.values
    : [];
  const afterValues = Array.isArray(state_after.values)
    ? state_after.values
    : [];
  const attributes = collectAttributes(state_before, state_after);

  attributes.forEach((attr) => {
    const attributeId = attr.id;
    const inputType = attr?.input_type || "text";
    const attributeName = attr.display_name || attr.name || attr.id;

    const isMultiple = isMultiValue(
      inputType,
      Boolean(attr.is_multiple_choice ?? false)
    );
    const beforeAttrValues = collectAttributeValues(
      beforeValues,
      attributeId,
      isMultiple
    );
    const afterAttrValues = collectAttributeValues(
      afterValues,
      attributeId,
      isMultiple
    );

    //handle toggle
    if (inputType === "toggle") {
      const beforeValue = beforeAttrValues.find(
        (v) => v.attribute_id === attributeId
      );
      const afterValue = afterAttrValues.find(
        (v) => v.attribute_id === attributeId
      );
      if (
        (beforeValue?.value === false ||
          beforeValue === null ||
          beforeValue === undefined) &&
        afterValue?.value === false
      ) {
        return;
      }
    }

    const hasBefore = beforeAttrValues.length > 0;
    const hasAfter = afterAttrValues.length > 0;

    if (!hasBefore && !hasAfter) {
      return;
    }

    if (hasBefore && hasAfter) {
      if (valuesAreDifferent(beforeAttrValues, afterAttrValues)) {
        const beforeValue:
          | ActivityHistoryValue
          | ActivityHistoryValue[]
          | null = isMultiple
          ? beforeAttrValues.length > 0
            ? beforeAttrValues
            : null
          : beforeAttrValues.length > 0
          ? beforeAttrValues[0]
          : null;

        const afterValue: ActivityHistoryValue | ActivityHistoryValue[] | null =
          isMultiple
            ? afterAttrValues.length > 0
              ? afterAttrValues
              : null
            : afterAttrValues.length > 0
            ? afterAttrValues[0]
            : null;

        subItems.push({
          attributeName,
          beforeValue,
          afterValue,
          inputType,
          isMultiple,
          addressMode: inputType === "address" ? attr.address_mode : undefined,
        });
      }
    } else if (!hasBefore && hasAfter) {
      const afterValue: ActivityHistoryValue | ActivityHistoryValue[] | null =
        isMultiple
          ? afterAttrValues
          : afterAttrValues.length > 0
          ? afterAttrValues[0]
          : null;

      if (hasMeaningfulValue(afterValue)) {
        subItems.push({
          attributeName,
          beforeValue: null,
          afterValue,
          inputType,
          isMultiple,
          addressMode: inputType === "address" ? attr.address_mode : undefined,
        });
      }
    } else if (hasBefore && !hasAfter) {
      const beforeValue: ActivityHistoryValue | ActivityHistoryValue[] | null =
        isMultiple
          ? beforeAttrValues
          : beforeAttrValues.length > 0
          ? beforeAttrValues[0]
          : null;

      if (hasMeaningfulValue(beforeValue)) {
        subItems.push({
          attributeName,
          beforeValue,
          afterValue: null,
          inputType,
          isMultiple,
          addressMode: inputType === "address" ? attr.address_mode : undefined,
        });
      }
    }
  });

  if (state_before.allow_custom_sku !== state_after.allow_custom_sku) {
    hasSkuConfigChange = true;
    const beforeValue = allowCustomSkuToValue(state_before.allow_custom_sku);
    const afterValue = allowCustomSkuToValue(state_after.allow_custom_sku);
    if (beforeValue && afterValue) {
      subItems.push({
        attributeName: t(BUYER_CUSTOM_SKU_ATTRIBUTE_NAME),
        beforeValue: beforeValue,
        afterValue: afterValue,
        inputType: "toggle",
      });
    }
  }

  // Process list_price_visibility
  if (
    state_before.list_price_visibility !== state_after.list_price_visibility
  ) {
    hasPricingVisibilityChange = true;
    const beforeValue = listPriceVisibilityToValue(
      state_before.list_price_visibility,
      t
    );
    const afterValue = listPriceVisibilityToValue(
      state_after.list_price_visibility,
      t
    );
    if (beforeValue && afterValue) {
      subItems.push({
        attributeName: t("Visibility of List Prices"),
        beforeValue: beforeValue,
        afterValue: afterValue,
        inputType: "single_select",
      });
    }
  }

  if (subItems.length === 0) {
    return null;
  }

  let targetName: string;
  if (hasStatusChange) {
    targetName = t("Status");
  } else if (hasSkuConfigChange) {
    targetName = t("SKUs Configuration");
  } else if (hasDocumentsChange) {
    targetName = t("Documents");
  } else if (hasPricingVisibilityChange) {
    targetName = t("Visibility of List Prices");
  } else {
    targetName = item.target_type || t("Group");
  }

  return {
    id: item.id,
    last_modified_by: item.last_modified_by,
    modified_at: item.modified_at,
    operation: t("Updated"),
    targetName,
    subItems,
  };
}

const ASSET_METADATA_KEYS = [
  "name",
  "asset_type",
  "asset_category",
  "host",
  "language",
  "document_type",
  "issue_date",
  "expiration_date",
  "renewal_date",
  "country",
  "region",
] as const;

function normalizeAssetValue(value: unknown): ValueType | null {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  return null;
}

export function hasAccessibilityChanges(
  beforeAsset: Assets | null,
  afterAsset: Assets | null
): boolean {
  if (!beforeAsset && !afterAsset) return false;

  const beforeAccessibility = beforeAsset
    ? beforeAsset.asset_type === "document"
      ? {
          allow_all_customers: beforeAsset.allow_all_customers,
          allow_all_distributors: beforeAsset.allow_all_distributors,
          allow_all_roles: beforeAsset.allow_all_roles,
          customers: beforeAsset.customers,
          roles: beforeAsset.roles,
          users: beforeAsset.users,
          visibility: beforeAsset.visibility,
        }
      : { visibility: beforeAsset.visibility }
    : null;

  const afterAccessibility = afterAsset
    ? afterAsset.asset_type === "document"
      ? {
          allow_all_customers: afterAsset.allow_all_customers,
          allow_all_distributors: afterAsset.allow_all_distributors,
          allow_all_roles: afterAsset.allow_all_roles,
          customers: afterAsset.customers,
          roles: afterAsset.roles,
          users: afterAsset.users,
          visibility: afterAsset.visibility,
        }
      : { visibility: afterAsset.visibility }
    : null;

  if (!beforeAccessibility && !afterAccessibility) return false;
  if (!beforeAccessibility || !afterAccessibility) return true;

  return (
    JSON.stringify(beforeAccessibility) !== JSON.stringify(afterAccessibility)
  );
}

export function getAssetMetadataDiffEntries({
  beforeAsset,
  afterAsset,
}: {
  beforeAsset: Assets | null;
  afterAsset: Assets | null;
}) {
  const entries: Array<{
    key: string;
    before: ValueType | null;
    after: ValueType | null;
    isName: boolean;
  }> = [];

  const mode =
    beforeAsset && afterAsset ? "updated" : beforeAsset ? "removed" : "added";

  ASSET_METADATA_KEYS.forEach((key) => {
    const beforeValue = normalizeAssetValue(beforeAsset?.[key as keyof Assets]);
    const afterValue = normalizeAssetValue(afterAsset?.[key as keyof Assets]);
    entries.push({
      key,
      before: mode === "added" ? null : beforeValue,
      after: mode === "removed" ? null : afterValue,
      isName: key === "name",
    });
  });

  const changedEntries = entries.filter(
    (entry) => entry.before !== entry.after
  );
  const hasAccessibility = hasAccessibilityChanges(beforeAsset, afterAsset);
  if (changedEntries.length === 0 && !hasAccessibility) {
    return [];
  }

  // When only accessibility changed, return an empty metadata list so callers
  // can still detect the accessibility-only update via hasAccessibilityChanges.
  return changedEntries;
}

export function processAssets(
  item: ActivityHistoryItem,
  t: TFunction
): ResultItem[] {
  const results: ResultItem[] = [];
  const state_before = item.state_before || {};
  const state_after = item.state_after || {};

  const beforeAssets: Assets[] = [];
  const afterAssets: Assets[] = [];

  if (state_before.assets && Array.isArray(state_before.assets)) {
    beforeAssets.push(...state_before.assets);
  } else if (state_before.asset) {
    beforeAssets.push(state_before.asset);
  }

  if (state_after.assets && Array.isArray(state_after.assets)) {
    afterAssets.push(...state_after.assets);
  } else if (state_after.asset) {
    afterAssets.push(state_after.asset);
  }

  const beforeAssetMap = new Map(
    beforeAssets.map((asset) => [asset.id, asset])
  );
  const afterAssetMap = new Map(afterAssets.map((asset) => [asset.id, asset]));

  afterAssetMap.forEach((afterAsset, assetId) => {
    if (!beforeAssetMap.has(assetId)) {
      const displayName = afterAsset.name || t("Document");
      results.push({
        id: `${item.id}-asset-added-${assetId}`,
        last_modified_by: item.last_modified_by,
        modified_at: item.modified_at,
        operation: t("Added"),
        targetName: displayName,
        subItems: [
          {
            attributeName: "",
            beforeValue: null,
            afterValue: afterAsset as Assets,
            inputType: "file",
          },
        ],
      });
    }
  });

  beforeAssetMap.forEach((beforeAsset, assetId) => {
    if (!afterAssetMap.has(assetId)) {
      const displayName = beforeAsset.name || t("Document");
      results.push({
        id: `${item.id}-asset-removed-${assetId}`,
        last_modified_by: item.last_modified_by,
        modified_at: item.modified_at,
        operation: t("Removed"),
        targetName: displayName,
        subItems: [
          {
            attributeName: "",
            beforeValue: beforeAsset as Assets,
            afterValue: null,
            inputType: "file",
          },
        ],
      });
    }
  });

  beforeAssetMap.forEach((beforeAsset, assetId) => {
    const afterAsset = afterAssetMap.get(assetId);
    if (afterAsset) {
      const entries = getAssetMetadataDiffEntries({
        beforeAsset,
        afterAsset,
      });
      const accessibilityChanged = hasAccessibilityChanges(
        beforeAsset,
        afterAsset
      );

      if (entries.length > 0 || accessibilityChanged) {
        const displayName =
          afterAsset.name || beforeAsset.name || t("Document");
        results.push({
          id: `${item.id}-asset-updated-${assetId}`,
          last_modified_by: item.last_modified_by,
          modified_at: item.modified_at,
          operation: t("Updated"),
          targetName: displayName,
          subItems: [
            {
              attributeName: "",
              beforeValue: beforeAsset as Assets,
              afterValue: afterAsset as Assets,
              inputType: "file",
            },
          ],
        });
      }
    }
  });

  return results;
}

export function processExternalLinks(
  item: ActivityHistoryItem,
  t: TFunction
): ResultItem | null {
  const beforeExternal = item.state_before?.external_link;
  const afterExternal = item.state_after?.external_link;

  const hasBefore = Boolean(beforeExternal);
  const hasAfter = Boolean(afterExternal);

  if (!hasBefore && !hasAfter) {
    return null;
  }

  let operation = t("Updated");
  if (!hasBefore && hasAfter) {
    operation = t("Added");
  } else if (hasBefore && !hasAfter) {
    operation = t("Removed");
  }

  const beforeUrl = beforeExternal?.url || "";
  const afterUrl = afterExternal?.url || "";
  const beforeName = beforeExternal?.name || "";
  const afterName = afterExternal?.name || "";
  const beforeVisible = beforeExternal?.is_visible;
  const afterVisible = afterExternal?.is_visible;

  const subItems: ResultItem["subItems"] = [];

  const toUrlValue = (url: string): ActivityHistoryValue | null => {
    if (!url) return null;
    return {
      attribute_id: "external_link_url",
      data_type: "url",
      id: `external-link-url-${url}`,
      priority: 0,
      value: url,
    };
  };

  const toNameValue = (name: string): ActivityHistoryValue | null => {
    if (!name) return null;
    return {
      attribute_id: "external_link_name",
      data_type: "text",
      id: `external-link-name-${name}`,
      priority: 0,
      value: name,
    };
  };

  const toToggleValue = (
    visible: boolean | undefined
  ): ActivityHistoryValue | null => {
    if (visible === undefined) return null;
    return {
      attribute_id: "external_link_is_visible",
      data_type: "boolean",
      id: `external-link-visible-${visible}`,
      priority: 0,
      value: visible,
    };
  };

  const isAddOrRemove = operation === t("Added") || operation === t("Removed");

  if (isAddOrRemove || beforeUrl !== afterUrl) {
    subItems.push({
      attributeName: t("URL"),
      beforeValue: toUrlValue(beforeUrl),
      afterValue: toUrlValue(afterUrl),
      inputType: "url",
    });
  }

  if (isAddOrRemove || beforeName !== afterName) {
    subItems.push({
      attributeName: t("Name"),
      beforeValue: toNameValue(beforeName),
      afterValue: toNameValue(afterName),
      inputType: "text",
    });
  }

  if (isAddOrRemove || beforeVisible !== afterVisible) {
    subItems.push({
      attributeName: t("Is Visible"),
      beforeValue: toToggleValue(beforeVisible),
      afterValue: toToggleValue(afterVisible),
      inputType: "toggle",
    });
  }

  if (subItems.length === 0) {
    return null;
  }

  const displayName = beforeName
    ? " - " + beforeName
    : afterName
    ? " - " + afterName
    : "";
  return {
    id: `${item.id}-external-link`,
    last_modified_by: item.last_modified_by,
    modified_at: item.modified_at,
    operation,
    targetName: t("External link") + displayName,
    subItems,
  };
}

function processApprovalSkuStatusChange(
  item: ActivityHistoryItem,
  t: TFunction
): ResultItem | null {
  const beforeSkuStatus = item.state_before?.sku_status;
  const afterSkuStatus = item.state_after?.sku_status;
  const statusMessage: React.ReactNode = React.createElement(
    React.Fragment,
    null,
    t(`${item.last_modified_by} changed the status of the SKU:`),
    " ",
    React.createElement("b", null, item.activity_args?.sku_number),
    " ",
    t("from"),
    " ",
    React.createElement("b", null, beforeSkuStatus),
    " ",
    t("to"),
    " ",
    React.createElement("b", null, afterSkuStatus)
  );

  return {
    id: `${item.id}-approval-sku-status-change`,
    last_modified_by: item.last_modified_by,
    modified_at: item.modified_at,
    operation: t("Updated"),
    targetName: t("SKU Status"),
    subItems: [],
    message: statusMessage,
  };
}

function processRelatedEntitiesUpdated(
  item: ActivityHistoryItem,
  t: TFunction
): ResultItem | null {
  const beforeSections = item.state_before?.sections ?? [];
  const afterSections = item.state_after?.sections ?? [];

  if (!beforeSections.length && !afterSections.length) {
    return null;
  }

  return {
    id: `${item.id}-related-entities-updated`,
    last_modified_by: item.last_modified_by,
    modified_at: item.modified_at,
    operation: t("Updated"),
    targetName: t("Related Entities"),
    subItems: [
      {
        attributeName: "",
        beforeValue: { sections: beforeSections },
        afterValue: { sections: afterSections },
        inputType: "related_entities_updated",
      },
    ],
  };
}

function processCompanyHierarchy(
  item: ActivityHistoryItem,
  t: TFunction
): ResultItem | null {
  const parentEntity =
    item.state_after?.parent_entity ?? item.state_before?.parent_entity;

  const subsidiaryActions = [
    ...(item.state_after?.subsidiary_actions || []),
    ...(item.state_before?.subsidiary_actions || []),
  ];

  const hasParentEntityChange = parentEntity && parentEntity !== null;

  const hasHierarchyChanges =
    hasParentEntityChange || subsidiaryActions.length > 0;

  if (!hasHierarchyChanges) {
    return null;
  }

  return {
    id: `${item.id}-company-hierarchy`,
    last_modified_by: item.last_modified_by,
    modified_at: item.modified_at,
    operation: t("Modified"),
    targetName: t("Company Hierarchy"),
    subItems: [
      {
        attributeName: t("Hierarchy"),
        beforeValue: {
          parentEntity: item.state_before?.parent_entity ?? {},
          subsidiaryActions: item.state_before?.subsidiary_actions ?? [],
        },
        afterValue: {
          parentEntity: item.state_after?.parent_entity ?? {},
          subsidiaryActions: item.state_after?.subsidiary_actions ?? [],
        },
        inputType: "company_hierarchy",
      },
    ],
  };
}

export function parseGroups(
  items: ActivityHistoryItem[],
  t: TFunction,
  targetType?: ActivityTargetType,
  getEntityLabel: (
    entityType: EntityType,
    form?: keyof MdmEntityDisplayNameForms
  ) => string = (entityType, form = "singular") =>
    getMdmEntityLabel(null, entityType, form, t)
): ResultItem[] {
  const results: ResultItem[] = [];
  items = collapseAssetActivityDuplicates(items);
  items.forEach((item) => {
    const hasProductNumber = Boolean(
      item.activity_args?.product_creation_request?.number ||
        item.state_after?.product_number
    );

    if (hasProductNumber) {
      const productCreationRequest =
        item.activity_args?.product_creation_request;

      if (!productCreationRequest) {
        const productNameValue: ActivityHistoryValue = {
          attribute_id: "product_name",
          data_type: "text",
          id: `${item.id}-product-name`,
          priority: 0,
          value: item.target_type || "",
        };

        results.push({
          id: `${item.id}-product-name`,
          last_modified_by: item.last_modified_by,
          modified_at: item.modified_at,
          operation: t("Modified"),
          targetName: t("General"),
          subItems: [
            {
              attributeName: t("Product Name"),
              beforeValue: null,
              afterValue: productNameValue,
              inputType: "text",
            },
          ],
        });
      }

      let message: string | React.ReactNode = t("Created the product");
      let targetName: string = "";
      let resultId: string = item.id;
      if (productCreationRequest) {
        const { form_name, number, id } = productCreationRequest;
        targetName = id;
        resultId = `${item.id}-product-creation`;
        message = React.createElement(
          React.Fragment,
          null,
          t("Created the product via"),
          " ",
          React.createElement(
            "b",
            null,
            (form_name || "Default Form") + " (" + number + ")"
          )
        );
      }

      results.push({
        id: resultId,
        last_modified_by: item.last_modified_by,
        modified_at: item.modified_at,
        operation: t("Created"),
        targetName,
        subItems: [],
        message,
      });
    }

    const labelByPage: Partial<Record<ActivityTargetType, string>> = {
      product: t("Product"),
      template: t("Product"),
      customer: getEntityLabel("customer", "singular"),
      supplier: getEntityLabel("supplier", "singular"),
      distributor: getEntityLabel("distributor", "singular"),
    };
    const label = (targetType && labelByPage[targetType]) || t("Product");

    // Detect entity profile creation: status goes to draft with no prior state
    const isEntityCreation =
      !hasProductNumber &&
      item.state_after?.status === "draft" &&
      !item.state_before?.status &&
      !hasGroupData(item);
    if (isEntityCreation) {
      results.push({
        id: `${item.id}-entity-creation`,
        last_modified_by: item.last_modified_by,
        modified_at: item.modified_at,
        operation: t("Created"),
        targetName: "",
        subItems: [],
        message: t("Created the {{label}}", { label }),
      });
    }

    const afterStatus = item.state_after?.status;
    const beforeStatus = item.state_before?.status;
    const statusTypes: Array<StatusTypes> = [
      "published",
      "unpublished",
      "archived",
      "discarded",
      "staged",
      "unpublished_staged",
    ];

    if (
      afterStatus &&
      beforeStatus !== afterStatus &&
      statusTypes.includes(afterStatus as StatusTypes)
    ) {
      let statusMessage: string;
      switch (afterStatus) {
        case "published":
          statusMessage = t("Published the {{label}}", { label });
          break;
        case "unpublished":
          statusMessage = t("Unpublished the {{label}}", { label });
          break;
        case "archived":
          statusMessage = t("Archived the {{label}}", { label });
          break;
        case "discarded":
          statusMessage = t("Discarded changes of the {{label}}", { label });
          break;
        default:
          statusMessage = "";
      }

      if (statusMessage) {
        results.push({
          id: `${item.id}-status-${afterStatus}`,
          last_modified_by: item.last_modified_by,
          modified_at: item.modified_at,
          operation: t("Updated"),
          targetName: "",
          subItems: [],
          message: statusMessage,
        });
      }
    }

    // process approval sku status change
    if (item.target_type === "approval_sku_status_change") {
      const approvalSkuStatusChangeResult = processApprovalSkuStatusChange(
        item,
        t
      );
      if (approvalSkuStatusChangeResult) {
        results.push(approvalSkuStatusChangeResult);
      }
    }

    if (item.target_type === "modified_company_hierarchy") {
      const companyHierarchyResult = processCompanyHierarchy(item, t);
      if (companyHierarchyResult) {
        results.push(companyHierarchyResult);
      }
    }

    if (item.target_type === "related_entities_updated") {
      const relatedEntitiesResult = processRelatedEntitiesUpdated(item, t);
      if (relatedEntitiesResult) {
        results.push(relatedEntitiesResult);
      }
      return; // don't fall through to generic processors for this target_type
    }

    // Process assets
    const assetResults = processAssets(item, t);
    results.push(...assetResults);

    // Process external links
    const externalLinkResult = processExternalLinks(item, t);
    if (externalLinkResult) {
      results.push(externalLinkResult);
    }

    if (hasGroupData(item)) {
      const result = processGroupItem(item, t);
      if (result) {
        results.push(result);
      }
    }
  });

  return results;
}

const collapseAssetActivityDuplicates = (
  items: ActivityHistoryItem[]
): ActivityHistoryItem[] => {
  const deduped: ActivityHistoryItem[] = [];
  let lastAssetKey: string | null = null;

  const isAssetActivity = (item: ActivityHistoryItem): boolean =>
    Boolean(item.state_after?.assets) ||
    Boolean(item.state_before?.assets) ||
    Boolean(item.state_after?.asset) ||
    Boolean(item.state_before?.asset);

  const toStatuslessKey = (item: ActivityHistoryItem): string =>
    JSON.stringify(item, (key, value) => {
      if (["status", "created_at", "modified_at", "id"].includes(key)) {
        return undefined;
      }
      return value;
    });

  items.forEach((item) => {
    if (isAssetActivity(item)) {
      const key = toStatuslessKey(item);
      if (key === lastAssetKey) {
        return;
      }
      deduped.push(item);
      lastAssetKey = key;
    } else {
      deduped.push(item);
      lastAssetKey = null;
    }
  });

  return deduped;
};
